import Api from "../../../../../../../../core/services/api/api";
import ApiResponseCodes from "../../../../../../../../core/models/static/api-response-codes";
import {ImportingDataTypes} from "../../types";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {toPartNumberUpper} from "../transformers/shared";

/**
 * Interactive correction sections for the "Product Category Assignment" import type.
 */
export const productCategoryAssignmentInteractiveSections = [
    {
        sectionKey: 'categoryNotFound',
        sectionTitle: 'Category Not Found',
        preventativeInteractionsInvalidityCodeBypass: [PreventionInvalidityCode.MISSING_REQUIRED_FIELDS],
        triggerErrorCodes: [ApiResponseCodes.importParentCategoryNotFound, ApiResponseCodes.importLeafCategoryNotFound, ApiResponseCodes.importCategoryMismatch],
        fields: [
            {
                key: 'parentCategoryTitle',
                label: 'Root Category',
                type: 'autocomplete',
                autocompleteProps: {
                    getOptionLabel: (o) => o?.title ?? '',
                    isOptionEqualToValue: (o, v) => o?.id === v?.id,
                },
                extractValue: (o) => o?.title ?? null,
                // Clearing leaf when root changes ensures the user re-selects a valid leaf.
                modifyRecord: (record, value) => ({
                    ...record,
                    parentCategoryTitle: value ?? record.parentCategoryTitle,
                    categoryTitle: '',
                }),
            },
            {
                key: 'categoryTitle',
                label: 'Leaf Category',
                type: 'autocomplete',
                // Cascade: filter leaf options to only those under the selected root
                cascadeSourceKey: 'parentCategoryTitle',
                filterOption: (option, rootValue, {allCategories = []} = {}) => {
                    // the option is the innermost leaf category, and the root category might have multiple levels of nesting, so we need to check the entire ancestry chain to see if the selected root is an ancestor of the option
                    let parentId = option.parentId;
                    while (parentId) {
                        if (parentId === rootValue?.id) {
                            return true;
                        }
                        const parentCategory = allCategories.find(c => c.id === parentId);
                        parentId = parentCategory?.parentId;
                    }
                    return false;
                },
                autocompleteProps: {
                    getOptionLabel: (o) => o?.fullTitle ?? o?.title ?? '',
                    isOptionEqualToValue: (o, v) => o?.id === v?.id,
                },
                // Extract the category's title string from the autocomplete object.
                extractValue: (o) => o?.title ?? null,
                modifyRecord: (record, value) => ({
                    ...record,
                    categoryTitle: value ?? record.categoryTitle,
                }),
            },
        ],
        getFieldOptions: (_apiRequest, {allCategories}) => {
            const categories = allCategories ?? [];
            const parentIds = new Set(categories.map(c => c.parentId).filter(Boolean));
            const roots = categories.filter(c => !c.parentId);
            const leaves = categories.filter(c => c.parentId && !parentIds.has(c.id));
            const fetchFullTitle = ((e) => {
                const ancestry = [];
                let parentId = e.parentId;
                while (parentId) {
                    const parentCategory = categories.find(c => c.id === parentId);
                    if (!parentCategory) break;
                    ancestry.unshift(parentCategory.title);
                    parentId = parentCategory.parentId;
                }
                // we do not want the root category in the full title, since it is already shown in the parentCategoryTitle field, so we slice(1) to remove it from the ancestry array before joining
                return [...ancestry, e.title].slice(1).join(' > ');
            })
            return {
                parentCategoryTitle: roots,
                categoryTitle: leaves.map(e => ({
                    ...e,
                    fullTitle: fetchFullTitle(e),
                })),
                allCategories: allCategories,
            };
        },
        describeRecord: (apiRequest) => `Part #${apiRequest.partNumber}: root="${apiRequest.parentCategoryTitle ?? ""}", leaf="${apiRequest.categoryTitle ?? ""}"`,
        isSimilarRecord: ({futureRecord, failingRecord, userValues}) => {
            // Match only if both root AND leaf match, regardless of what the user chose to update
            return (
                futureRecord.parentCategoryTitle === failingRecord.parentCategoryTitle &&
                futureRecord.categoryTitle === failingRecord.categoryTitle
            );
        },
    },
    {
        sectionKey: 'productNotFound',
        sectionTitle: 'Product Not Found',
        preventativeInteractionsInvalidityCodeBypass: [PreventionInvalidityCode.INVALID_PART],
        triggerErrorCodes: [ApiResponseCodes.importProductNotFound],
        fields: [
            {
                key: 'partNumber',
                label: 'Part Number',
                type: 'autocomplete',
                getOptions: async (cancelToken, keywords) => {
                    const response = await Api.externalPartAutoFill({
                        keyword: keywords,
                        pagination: {pageSize: 20, currentPage: 1},
                    }, cancelToken);
                    return response?.data?.items ?? [];
                },
                autocompleteProps: {
                    getOptionLabel: (o) => o?.partNo ?? '',
                    isOptionEqualToValue: (o, v) => o?.internalId === v?.internalId,
                    keywordsBased: true,
                },
                extractValue: (o) => o?.partNo ?? null,
                modifyRecord: (record, value) => ({
                    ...record,
                    partNumber: toPartNumberUpper(value),
                }),
            },
        ],
        describeRecord: (apiRequest) => `Category: ${apiRequest.parentCategoryTitle}${apiRequest.categoryTitle ? ` > ${apiRequest.categoryTitle}` : ""} | Invalid Part: ${apiRequest.partNumber}`,
        isSimilarRecord: ({futureRecord, failingRecord, userValues}) => {
            // Match if the future record's partNumber matches the failing record's partNumber
            return futureRecord.partNumber === failingRecord.partNumber;
        },
    },
];

export const correctiveInteraction = {
    datasourceId: ImportingDataTypes.productCategoryAssignment,
    sections: productCategoryAssignmentInteractiveSections,
};
